import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Spacer } from "../../components/Layout/Layout";
import {
  breakpoints,
  useWindowDimensions,
} from "../../core/hooks/dimensionProvider";
import Button from "../../ui-lib/components/Button/Button";
import TextInput from "../../ui-lib/components/Inputs/TextInput";
import PasswordInput from "../../ui-lib/components/Inputs/PasswordInput";
import useTranslations from "../../core/i18n/useTranslations";
import useBranding from "../../core/branding/useBranding";
import Divider from "../../ui-lib/components/Divider/Divider";

import styles from "./Login.module.css";
import { useADLoginLink } from "../../core/hooks/useADLoginLink";

export interface Step1FormData {
  username: string;
  password: string;
}

export const Step1 = ({
  onSubmit,
}: {
  onSubmit: (formData: Step1FormData) => void;
}) => {
  const t = useTranslations();
  const brandingData = useBranding();
  const { width } = useWindowDimensions();
  const getADLoginLink = useADLoginLink();

  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
  } = useForm<Step1FormData>();

  const onFocusChange = () => {
    const token = JSON.parse(window.localStorage.getItem("auth-token") ?? "{}");
    if (token?.token) {
      window.location.replace("/adminportal");
    }
  };

  useEffect(() => {
    window.addEventListener("focus", onFocusChange);
    return () => window.removeEventListener("focus", onFocusChange);
  }, []);

  return (
    <>
      <h1>{t("Common:login")}</h1>
      <Spacer size={32} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="username"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              dataCy="username_field"
              label={t("Common:username")}
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors.username && t("Errors:input_field_required")
              }
            />
          )}
        />
        <Spacer size={16} />
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <PasswordInput
              dataCy="password_field"
              label={t("Common:login_input_password")}
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors.password && t("Errors:input_field_required")
              }
            />
          )}
        />
        <div className={styles.loginButtons}>
          <Button
            dataCy="log_in_button"
            className={styles.loginBtn}
            type="submit"
            text={t("Common:login")}
            loading={isSubmitting}
          />
          {brandingData.isForgotPasswordEnabled && (
            <>
              {width < breakpoints.desktop && <Spacer size={16} />}
              <Link
                data-cy="forgot_password_link"
                className={styles.forgotBtn}
                to="/adminportal/forgotPassword"
              >
                {t("Common:forgot_password")}
              </Link>
            </>
          )}
        </div>

        {width >= breakpoints.desktop && (
          <>
            <Spacer size={32} />
            <Divider />
            <Spacer size={32} />
          </>
        )}

        <Link to={{ pathname: getADLoginLink() }} target="_blank" tabIndex={-1}>
          <Button
            dataCy="ad_login_button"
            className={styles.adBtn}
            image="login"
            text={t("Common:ad_login_link_text")}
            variant="border-link"
            iconVariant="border-link"
          />
        </Link>
      </form>
    </>
  );
};
